import { formatCurrency } from '@airship-platform/airship-patterns';
import { FlexBox } from '@airship-platform/jam-web/components';
import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import z from 'zod';
import { UniversalUpgradeProduct } from '../../data-access';
import { UniversalUpgradeProductCard } from '../upgrade-product-card';
import UpgradeSelectorModal from './UpgradeSelectorModal';

const installationTypeOptions = [
  { value: 'REPAIR', label: 'Repair' },
  { value: 'REPLACE', label: 'Replace' },
] as const;

const proposalTypesOptions = [
  { value: 'HVAC', label: 'HVAC' },
  { value: 'GENERATOR', label: 'Generator' },
  { value: 'WATER_QUALITY', label: 'Water Quality' },
  { value: 'PLUMBING', label: 'Plumbing' },
] as const;

const UpgradesSelectorSchema = z.object({
  selectedUpgradeIds: z.uuid().array(),
  proposalType: z.enum(['HVAC', 'GENERATOR', 'WATER_QUALITY', 'PLUMBING']),
  installationType: z.enum(['REPLACE', 'REPAIR']),
});

export type UpgradesSelectorInputs = z.infer<typeof UpgradesSelectorSchema>;

type UpgradesSelectorProps = {
  formId: string;
  defaultValues: UpgradesSelectorInputs;
  upgrades: UniversalUpgradeProduct[];
  isLoading: boolean;
  onSubmit: (data: UpgradesSelectorInputs) => void;
};

const UpgradesSelector: React.FC<UpgradesSelectorProps> = ({
  formId,
  defaultValues,
  upgrades,
  onSubmit,
}) => {
  const [open, setOpen] = useState(false);
  const [upgrade, setUpgrade] = useState<UniversalUpgradeProduct | null>(null);

  const { control, watch, handleSubmit, setValue } = useForm({
    defaultValues,
    mode: 'onSubmit',
    resolver: zodResolver(UpgradesSelectorSchema),
  });

  const formValues = watch();

  const filteredUpgrades = upgrades.filter((upgrade) =>
    upgrade.installationTypes.length || upgrade.proposalTypes.length
      ? upgrade.installationTypes.includes(formValues.installationType) &&
        upgrade.proposalTypes.includes(formValues.proposalType)
      : true
  );

  const handleSeeMore = (upgrade: UniversalUpgradeProduct) => {
    setUpgrade(upgrade);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setUpgrade(null);
  };

  const onSwitchChange = (selectedUpgradeIds: string[], upgradeId: string) => {
    if (selectedUpgradeIds.includes(upgradeId)) {
      setValue(
        'selectedUpgradeIds',
        selectedUpgradeIds.filter((id) => id !== upgradeId)
      );
    } else {
      setValue('selectedUpgradeIds', [...selectedUpgradeIds, upgradeId]);
    }
  };

  const onSubmitForm = (data: UpgradesSelectorInputs) => {
    const upgradeIds = new Set(
      filteredUpgrades.map((u) => u.pricebookUniversalUpgradeId)
    );

    return onSubmit({
      ...data,
      selectedUpgradeIds: data.selectedUpgradeIds.filter((id) =>
        upgradeIds.has(id)
      ),
    });
  };

  return (
    <Box
      component={'form'}
      id={formId}
      onSubmit={handleSubmit(onSubmitForm)}
      autoComplete="off"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        width: '100%',
      }}
    >
      <UpgradeSelectorModal
        open={open}
        onClose={handleClose}
        upgrade={upgrade}
      />

      <FlexBox flexDirection="column" gap={2}>
        <FlexBox gap={2} flexDirection={['column', 'row']}>
          <Controller
            name="installationType"
            control={control}
            render={({ field }) => (
              <TextField {...field} select label="Installation Type">
                {installationTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          <Controller
            name="proposalType"
            control={control}
            render={({ field }) => (
              <TextField {...field} select label="Proposal Type">
                {proposalTypesOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </FlexBox>

        <Controller
          name={'selectedUpgradeIds'}
          control={control}
          render={({ field }) => (
            <FlexBox flexDirection={'column'} gap={2}>
              {filteredUpgrades.map((upgrade) => (
                <FlexBox
                  key={upgrade.pricebookUniversalUpgradeId}
                  justifyContent="space-between"
                >
                  <UniversalUpgradeProductCard
                    upgrade={upgrade}
                    onSeeMore={() => handleSeeMore(upgrade)}
                  >
                    <Typography variant={'subtitle1'}>
                      {formatCurrency(
                        upgrade.prices.find(
                          (p) => p.priceType === upgrade.defaultPriceType
                        )?.value || 0
                      )}
                    </Typography>
                  </UniversalUpgradeProductCard>
                  <FlexBox alignItems="center" gap={2}>
                    <Switch
                      checked={field.value.includes(
                        upgrade.pricebookUniversalUpgradeId
                      )}
                      onChange={() =>
                        onSwitchChange(
                          field.value,
                          upgrade.pricebookUniversalUpgradeId
                        )
                      }
                      color="info"
                    />
                    <Typography variant="body2" color="blue">
                      Show
                    </Typography>
                  </FlexBox>
                </FlexBox>
              ))}
            </FlexBox>
          )}
        />
      </FlexBox>
    </Box>
  );
};

export default UpgradesSelector;
